import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Space } from 'antd';
import { UserSwitchOutlined } from '@ant-design/icons';
import { getStoredImpersonation } from './impersonationStorage';
import { stopImpersonation } from './useImpersonationActions';

/**
 * Полоса-предупреждение поверх интерфейса, пока активна эмуляция пользователя.
 * Единственный способ выйти — кнопка здесь (POST /impersonation/stop + reload).
 */
export function ImpersonationBanner() {
  const [stopping, setStopping] = useState(false);
  const stored = getStoredImpersonation();
  const alertRef = useRef<HTMLDivElement>(null);

  // html::after рисует fixed-подложку под sticky-хедером на всю высоту
  // header-а (см. _reset.scss, --header-backdrop-top), закрывая собой
  // всё, что окажется выше хедера — в т.ч. этот баннер. Сдвигаем подложку
  // вниз на реальную высоту баннера, пока он показан.
  useEffect(() => {
    const root = document.documentElement;
    if (!stored) {
      root.style.removeProperty('--header-backdrop-top');
      return;
    }

    const el = alertRef.current;
    if (!el) return;

    const updateOffset = () => {
      root.style.setProperty('--header-backdrop-top', `${el.offsetHeight}px`);
    };
    updateOffset();

    const resizeObserver = new ResizeObserver(updateOffset);
    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
      root.style.removeProperty('--header-backdrop-top');
    };
  }, [stored]);

  if (!stored) return null;

  const handleStop = async () => {
    setStopping(true);
    try {
      await stopImpersonation();
    } finally {
      setStopping(false);
    }
  };

  return (
    <div ref={alertRef} style={{ position: 'relative', zIndex: 1006 }}>
      <Alert
        type="warning"
        banner
        icon={<UserSwitchOutlined />}
        message={
          <Space size="small" wrap>
            <span>
              Вы работаете как <strong>{stored.targetName}</strong> ({stored.targetEmail})
            </span>
          </Space>
        }
        action={
          <Button size="small" danger loading={stopping} onClick={handleStop}>
            Выйти из эмуляции
          </Button>
        }
        style={{ borderRadius: 0 }}
      />
    </div>
  );
}

export default ImpersonationBanner;
